import classNames from 'classnames';
import Button from '../../atoms/button';
import Card from '../../atoms/card';
import SVGIcon from '../../atoms/icons';
import TextArea from '../../atoms/text-area';
import Text from '../../atoms/text';
import Input from '../../atoms/input';
import RadioOption from '../../atoms/radio';

import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { GeneralProblemForm } from './general-problem';
import { NotificationToast } from '../notification-toast';
import { useRouteLoaderData } from 'react-router-dom';
import { HomeLoaderData } from '../../../types/homeLoaderData';

import Modal from '../modal';
import ContainerService, {
	IContainer,
} from '../../../services/Container.service';
import Label from '../../atoms/text/label';
import MonitoringService from '../../../services/Monitoring.service';

export type TempStatus =
	| 'Temperatura Regular'
	| 'Temperatura em Alerta'
	| 'Temperatura Crítica'
	| 'Problema Crítico'
	| 'Sem Alimentação'
	| 'Falta de Energia';

export type SetPointError = 'Coolingdown' | 'Defrost';

export interface MonitoringForm {
	label: string;
	setPoint: string;
	temperature: string;
	cntrStatus: TempStatus;
	tempStatus?: SetPointError;
	observation: string;
	isChecked: boolean;
	idContainer: number;
	id: number;
	images: string[];
	files: File[];
	humidity?: number;
	ventilation?: number;
	atmosferaControlada?: boolean;
	nivelCo2?: number | string | null;
	return?: number | string;
	supply?: number | string;
	returnSetPoint?: number | string | null;
	supplySetPoint?: number | string | null;
	possuiMonitoramentoLocal?: boolean;
	reparoPreventivo?: boolean;
	container?: IContainer;
}

interface MonitoringContainersProps {
	data: MonitoringForm[];
	setData: React.Dispatch<React.SetStateAction<MonitoringForm[]>>;
	generalProblemData: GeneralProblemForm;
	search: string;
	disabled: boolean;
}

const getTempStatus = (tempCurrent: string, tempInitial: string) => {
	const tempDiff = Math.abs(Number(tempCurrent) - Number(tempInitial));
	return tempDiff >= 4
		? 'Temperatura Crítica'
		: tempDiff > 2
			? 'Temperatura em Alerta'
			: 'Temperatura Regular';
};

const obterStatusPorDiferenca = (
	valorAtual?: number | string | null,
	valorConfigurado?: number | string | null
): TempStatus => {
	if (
		valorAtual === undefined ||
		valorAtual === null ||
		valorAtual === '' ||
		valorConfigurado === undefined ||
		valorConfigurado === null ||
		valorConfigurado === ''
	) {
		return 'Temperatura Regular';
	}

	return getTempStatus(String(valorAtual), String(valorConfigurado));
};

const obterStatusSupplyReturn = (item: MonitoringForm): TempStatus => {
	const supplyVazio = item.supply == null;
	const returnVazio = item.return == null;

	if (
		![
			'Temperatura Regular',
			'Temperatura em Alerta',
			'Temperatura Crítica',
		].includes(item.cntrStatus)
	) {
		return item.cntrStatus;
	}

	if (supplyVazio && returnVazio) return 'Problema Crítico';

	const statusSupply = obterStatusPorDiferenca(item.supply, item.setPoint);
	const statusReturn = obterStatusPorDiferenca(item.return, item.setPoint);

	if (
		statusSupply === 'Temperatura Crítica' ||
		statusReturn === 'Temperatura Crítica'
	) {
		return 'Temperatura Crítica';
	}

	if (
		statusSupply === 'Temperatura em Alerta' ||
		statusReturn === 'Temperatura em Alerta'
	) {
		return 'Temperatura em Alerta';
	}

	return 'Temperatura Regular';
};

const formatarSetPoint = (setPoint: string | number | null | undefined) => {
	if (setPoint === null) return 'DRY';
	const setPointNumerico = Number(setPoint);
	return Number.isFinite(setPointNumerico)
		? setPointNumerico.toFixed(1)
		: setPoint;
};

const AWS_S3BUCKET_ASSETS_URL =
	'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/';

const MonitoringContainers = ({
	data,
	setData,
	generalProblemData,
	search,
	disabled,
}: MonitoringContainersProps) => {
	const { user } = useRouteLoaderData('home') as HomeLoaderData;
	const { t } = useTranslation();

	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [isConfigModalVisible, setIsConfigModalVisible] = useState<boolean>(false);
	const [modalData, setModalData] = useState<MonitoringForm>();
	const [statusAbertoIndex, setStatusAbertoIndex] = useState<number>();
	const supplyReturnAtivado = user.client?.system_configs?.supplyReturnAtivado === true;

	const loadMonitorings = useCallback(async () => {
		if (!user || user.type === 'User') return;
		const data = await MonitoringService.getAllByOwner(user.email);
		if (!data) setData([]);
		else setData(data);
	}, [user]);

	useEffect(() => {
		loadMonitorings();
	}, [loadMonitorings]);

	useEffect(() => {
		setData((prev) =>
			prev.map((item) => ({
				...item,
				cntrStatus: generalProblemData.isProblem
					? 'Falta de Energia'
					: item.cntrStatus === 'Falta de Energia'
						? supplyReturnAtivado
							? obterStatusSupplyReturn({ ...item, cntrStatus: 'Temperatura Regular' })
							: getTempStatus(item.setPoint, item.temperature)
						: item.cntrStatus,
				tempStatus: generalProblemData.isProblem ? undefined : item.tempStatus,
				reparoPreventivo: generalProblemData.isProblem ? false : item.reparoPreventivo,
			}))
		);
	}, [generalProblemData.isProblem]);

	const handleTemperature = (
		e: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const valor = e.target.value.replace(',', '.');
		if (!/^-?\d*(?:\.\d*)?$/.test(valor)) return;
		const dataUpdated = [...data];
		dataUpdated[index].temperature = valor;
		dataUpdated[index].cntrStatus = generalProblemData.isProblem
			? 'Falta de Energia'
			: getTempStatus(
				dataUpdated[index].temperature,
				dataUpdated[index].setPoint
			);
		setData(dataUpdated);
	};

	const handleDescription = (
		e: React.ChangeEvent<HTMLTextAreaElement>,
		index: number
	) => {
		const dataUpdated = [...data];
		dataUpdated[index].observation = e.target.value;
		setData(dataUpdated);
	};

	const handleIsDone = (index: number) => {
		const dataUpdated = [...data];
		dataUpdated[index].isChecked = !dataUpdated[index].isChecked;
		setData(dataUpdated);
	};

	const handleTempStatus = (status: TempStatus, index: number) => {
		if (generalProblemData.isProblem) return;
		const dataUpdated = [...data];
		if (status === 'Temperatura Regular') {
			const valorPadrao = Number(dataUpdated[index].setPoint);
			if (Number.isFinite(valorPadrao)) {
				dataUpdated[index].supply ??= valorPadrao;
				dataUpdated[index].return ??= valorPadrao;
			}
			dataUpdated[index].cntrStatus = status;
			dataUpdated[index].tempStatus = undefined;
			setData(dataUpdated);
			setStatusAbertoIndex(undefined);
			return;
		}
		if (dataUpdated[index].cntrStatus === status) {
			dataUpdated[index].cntrStatus = getTempStatus(
				dataUpdated[index].temperature,
				dataUpdated[index].setPoint
			);
		} else dataUpdated[index].cntrStatus = status;
		setData(dataUpdated);
		setStatusAbertoIndex(undefined);
	};

	const handleTempError = (value: string, index: number) => {
		if (generalProblemData.isProblem) return;
		const dataUpdated = [...data];
		if (data[index].tempStatus !== value)
			dataUpdated[index].tempStatus = value as SetPointError;
		else dataUpdated[index].tempStatus = undefined;
		setData(dataUpdated);
	};

	const onPlusClick = (index: number) => {
		const dataUpdated = [...data];
		dataUpdated[index].temperature = String(
			(Number(dataUpdated[index].temperature) + 1).toFixed(1)
		);
		dataUpdated[index].cntrStatus = generalProblemData.isProblem
			? 'Falta de Energia'
			: getTempStatus(
				dataUpdated[index].temperature,
				dataUpdated[index].setPoint
			);
		setData(dataUpdated);
	};

	const onMinusClick = (index: number) => {
		const dataUpdated = [...data];
		dataUpdated[index].temperature = String(
			(Number(dataUpdated[index].temperature) - 1).toFixed(1)
		);
		dataUpdated[index].cntrStatus = generalProblemData.isProblem
			? 'Falta de Energia'
			: getTempStatus(
				dataUpdated[index].temperature,
				dataUpdated[index].setPoint
			);
		setData(dataUpdated);
	};

	const handleModalVisibility = (
		visible: boolean,
		modalData?: MonitoringForm
	) => {
		setIsModalVisible(visible);
		setModalData(modalData);
	};

	const handleDeleteContainer = async () => {
		if (!modalData) return;
		const response = await ContainerService.deactivate([
			modalData.idContainer,
		]);
		if (!response) return;
		setData((prev) =>
			prev.filter((item) => item.idContainer !== modalData.idContainer)
		);
		handleModalVisibility(false);
		NotificationToast.Success(
			`${t('port_management.container')} ${t('alert.success.delete')}`
		);
	};

	const handlePercentage = (
		e: React.ChangeEvent<HTMLInputElement>,
		name: 'humidity' | 'ventilation' | 'nivelCo2',
		index: number
	) => {
		const dataUpdated = [...data];
		const itemAtualizado = {
			...dataUpdated[index],
			[name]: e.target.value === '' ? undefined : Number(e.target.value),
		};

		if (name === 'nivelCo2') {
			itemAtualizado.atmosferaControlada = Number(itemAtualizado.nivelCo2) >= 1;
		}

		dataUpdated[index] = itemAtualizado;
		setData(dataUpdated);
		if (modalData?.idContainer === itemAtualizado.idContainer) {
			setModalData(itemAtualizado);
		}
	};

	const handleSupplyReturn = (
		e: React.ChangeEvent<HTMLInputElement>,
		name: 'return' | 'supply',
		index: number
	) => {
		const valor = e.target.value.replace(',', '.');
		if (!/^-?\d*(?:\.\d*)?$/.test(valor)) return;
		const dataUpdated = [...data];
		dataUpdated[index][name] = valor === '' ? undefined : valor;
		dataUpdated[index].cntrStatus = generalProblemData.isProblem
			? 'Falta de Energia'
			: obterStatusSupplyReturn(dataUpdated[index]);
		setData(dataUpdated);
	};

	const handleAtmosferaControlada = (index: number) => {
		const dataUpdated = [...data];
		const itemAtualizado = {
			...dataUpdated[index],
			atmosferaControlada: !dataUpdated[index].atmosferaControlada,
		};
		dataUpdated[index] = itemAtualizado;
		setData(dataUpdated);
		if (modalData?.idContainer === itemAtualizado.idContainer) {
			setModalData(itemAtualizado);
		}
	};

	const handleReparoPreventivo = (index: number) => {
		if (generalProblemData.isProblem) return;
		const dataUpdated = [...data];
		dataUpdated[index].reparoPreventivo =
			!dataUpdated[index].reparoPreventivo;
		setData(dataUpdated);
	};

	const handleConfigModalVisibility = (
		visible: boolean,
		modalData?: MonitoringForm
	) => {
		setIsConfigModalVisible(visible);
		setModalData(modalData);
	};

	const renderStatusIcone = (item: MonitoringForm) => {
		if (item.reparoPreventivo) {
			return (
				<img
					className="w-15 h-auto"
					src={`${AWS_S3BUCKET_ASSETS_URL}preventive.png`}
					alt={t('monitor.preventive_repair')}
				/>
			);
		}

		if (item.cntrStatus === 'Temperatura Regular') {
			return <SVGIcon className="w-12 h-auto" icon="Check" />;
		}

		if (item.cntrStatus === 'Problema Crítico') {
			return (
				<img
					className="w-25 h-auto"
					src={`${AWS_S3BUCKET_ASSETS_URL}notification_important_outlined.png`}
					alt="Problema Crítico"
				/>
			);
		}

		if (item.cntrStatus === 'Sem Alimentação') {
			return <SVGIcon className="w-12 h-auto" icon="NoEnergy" />;
		}

		if (item.cntrStatus === 'Falta de Energia') {
			return <SVGIcon className="w-12 h-auto" icon="Socket" />;
		}

		if (item.cntrStatus === 'Temperatura Crítica') {
			return <SVGIcon className="w-12 h-auto fill-white" icon="X" />;
		}

		return <SVGIcon className="w-12 h-auto stroke-white" icon="Error" />;
	};

	const renderStatusOpcaoIcone = (item: MonitoringForm) => {
		if (!item.reparoPreventivo) return renderStatusIcone(item);

		const iconesReparoPreventivo: Partial<Record<TempStatus, string>> = {
			'Problema Crítico': 'notification_important_preventive.png',
			'Sem Alimentação': 'power_off_preventive.png',
			'Falta de Energia': 'outlet_prev.png',
		};
		const iconeReparoPreventivo = iconesReparoPreventivo[item.cntrStatus];

		if (!iconeReparoPreventivo) return renderStatusIcone(item);

		return (
			<img
				className="w-15 h-auto"
				src={`${AWS_S3BUCKET_ASSETS_URL}${iconeReparoPreventivo}`}
				alt={t('monitor.preventive_repair')}
			/>
		);
	};

	const getStatusClassName = (status: TempStatus) =>
		classNames(
			'border-default rounded-md flex items-center justify-center cursor-pointer select-none',
			{
				'bg-primary-light': status === 'Temperatura Regular',
				'bg-black stroke-red': status === 'Problema Crítico',
				'bg-yellow stroke-black': status === 'Sem Alimentação',
				'bg-red fill-red': status === 'Falta de Energia',
				'bg-red': status === 'Temperatura Crítica',
				'bg-yellow': status === 'Temperatura em Alerta',
			}
		);

	const renderStatusOpcao = (
		status: TempStatus,
		item: MonitoringForm,
		index: number,
		className = 'size-20'
	) => (
		<div
			className={classNames(getStatusClassName(status), className, {
				'border-primary': item.cntrStatus === status,
			})}
			onClick={(e) => {
				e.stopPropagation();
				handleTempStatus(status, index);
				}}
			>
				{renderStatusOpcaoIcone({ ...item, cntrStatus: status })}
			</div>
		);

	const renderStatusSeletor = (
		item: MonitoringForm,
		index: number,
		className = 'size-20'
	) => (
		<div className="relative">
			<div
			className={classNames(
				getStatusClassName(item.cntrStatus),
				className,
				{
					'pointer-events-none': user.type !== 'User',
				}
			)}
			onClick={() =>
				setStatusAbertoIndex((prev) =>
					prev === index ? undefined : index
				)
			}
			>
				{renderStatusIcone(item)}
			</div>
			{statusAbertoIndex === index && (
				<div
					className={classNames(
						'absolute top-full z-20 mt-2 flex gap-2 rounded-md bg-white p-2 shadow-lg',
						{
							'right-0': supplyReturnAtivado,
							'left-0': !supplyReturnAtivado,
						}
					)}
				>
					{renderStatusOpcao(
						'Temperatura Regular',
						item,
						index
					)}
					{renderStatusOpcao('Problema Crítico', item, index)}
					{renderStatusOpcao('Sem Alimentação', item, index)}
					{renderStatusOpcao('Falta de Energia', item, index)}
				</div>
			)}
		</div>
	);

	const configuracaoPreenchida = (item: MonitoringForm) =>
		item.atmosferaControlada === true ||
		[item.ventilation, item.humidity, item.nivelCo2].some(
			(valor) => Number(valor) >= 1
		);

	const deveExibirReturnAcima = (item: MonitoringForm) => {
		const setPointNumerico = Number(item.setPoint);
		return (
			Number.isFinite(setPointNumerico) &&
			setPointNumerico >= -60 &&
			setPointNumerico <= -5
		);
	};

	const renderSupplyReturnCard = (item: MonitoringForm, index: number) => {
		const statusSupplyReturn = obterStatusSupplyReturn(item);

		return (
		<Card
			className={classNames(
				'grid grid-cols-3 gap-x-7 gap-y-5 p-6 max-w-[32rem]',
				{
					hidden: !item.label.includes(search),
					'border-2 border-dashed border-red':
						item.possuiMonitoramentoLocal === false,
				}
			)}
			key={`monitoring-${index}`}
		>
			<div
				className={classNames(
					'rounded-md size-28 cursor-pointer flex items-center justify-center',
					{
						'bg-green': item.isChecked,
						'border-default border-dashed': !item.isChecked,
						'pointer-events-none': user.type !== 'User',
					}
				)}
				onClick={() => handleIsDone(index)}
			>
				{item.isChecked && (
					<SVGIcon className="w-16 h-auto" icon="Check" />
				)}
			</div>
			{user.type === 'User' && (
				<>
					<div
						className={
							'border-default rounded-md size-28 flex items-center justify-center cursor-pointer'
						}
						onClick={() => handleModalVisibility(true, item)}
					>
						<SVGIcon
							className="w-16 h-auto stroke-black"
							icon="Delete"
						/>
					</div>
					<Label
						className="border-default rounded-md size-28 flex items-center justify-center cursor-pointer"
						htmlFor={`container-${index}-img`}
					>
						<input
							className="hidden"
							id={`container-${index}-img`}
							type="file"
							accept="image/*"
							onChange={(e) => handleImageChange(e, index)}
							multiple
						/>
						<SVGIcon className="w-16 h-auto" icon="Camera" />
					</Label>
				</>
			)}
			<div className="flex flex-col items-center">
				<Text className="font-bold">Setpoint</Text>
				<Input
					className="size-28 px-0 text-center text-3xl font-bold"
					value={formatarSetPoint(item.setPoint)}
					disabled
				/>
			</div>
			<div className="grid grid-rows-2">
				<div
					className={classNames('flex flex-col items-center', {
						'order-1': !deveExibirReturnAcima(item),
						'order-2': deveExibirReturnAcima(item),
					})}
				>
					<Text className="font-bold">Supply</Text>
					<Input
						className={classNames(
							'h-14 w-28 px-0 text-center text-3xl font-bold',
							{
								'rounded-b-none': !deveExibirReturnAcima(item),
								'rounded-t-none': deveExibirReturnAcima(item),
							}
						)}
						name="supply"
						value={
							statusSupplyReturn === 'Problema Crítico'
								? 'OFF'
								: item.supply ?? ''
						}
						onChange={(e) => handleSupplyReturn(e, 'supply', index)}
						type="text"
						inputMode="decimal"
						disabled={
							user.type !== 'User' ||
							statusSupplyReturn === 'Problema Crítico'
						}
					/>
				</div>
				<div
					className={classNames('flex flex-col items-center', {
						'order-1': deveExibirReturnAcima(item),
						'order-2': !deveExibirReturnAcima(item),
					})}
				>
					<Text className="font-bold">Return</Text>
					<Input
						className={classNames(
							'h-14 w-28 px-0 text-center text-3xl font-bold',
							{
								'rounded-b-none': deveExibirReturnAcima(item),
								'rounded-t-none': !deveExibirReturnAcima(item),
							}
						)}
						name="return"
						value={
							statusSupplyReturn === 'Problema Crítico'
								? 'OFF'
								: item.return ?? ''
						}
						onChange={(e) => handleSupplyReturn(e, 'return', index)}
						type="text"
						inputMode="decimal"
						disabled={
							user.type !== 'User' ||
							statusSupplyReturn === 'Problema Crítico'
						}
					/>
				</div>
			</div>
			{renderStatusSeletor(
				{ ...item, cntrStatus: statusSupplyReturn },
				index,
				'size-28'
			)}
			{statusSupplyReturn === 'Temperatura Crítica' && (
				<div className="flex gap-x-4 col-span-full place-self-center">
					<RadioOption
						id={`${item.id}-coolingdown-${index}`}
						name={`${item.id}-${index}`}
						label="Coolingdown"
						value="Coolingdown"
						checked={item.tempStatus === 'Coolingdown'}
						onClick={() => handleTempError('Coolingdown', index)}
						onChange={() => {}}
						disabled={user.type !== 'User'}
					/>
					<RadioOption
						id={`${item.id}-defrost-${index}`}
						name={`${item.id}-${index}`}
						label="Defrost"
						value="Defrost"
						checked={item.tempStatus === 'Defrost'}
						onClick={() => handleTempError('Defrost', index)}
						onChange={() => {}}
						disabled={user.type !== 'User'}
					/>
				</div>
			)}
			<Label
				className={classNames(
					'flex items-center gap-2 col-span-full cursor-pointer select-none w-fit place-self-center',
					{
						'text-primary': item.reparoPreventivo,
						'text-gray': !item.reparoPreventivo,
						'pointer-events-none': user.type !== 'User',
					}
				)}
				htmlFor={`reparo-preventivo-supply-return-${index}`}
			>
				<input
					className="relative appearance-none w-4 h-4 text-primary border border-gray-border rounded cursor-pointer checked:border-primary checked:after:absolute checked:after:bg-primary checked:after:size-2 checked:after:rounded checked:after:top-1/2 checked:after:left-1/2 checked:after:-translate-x-1/2 checked:after:-translate-y-1/2"
					id={`reparo-preventivo-supply-return-${index}`}
					type="checkbox"
					checked={!!item.reparoPreventivo}
					onChange={() => handleReparoPreventivo(index)}
					disabled={user.type !== 'User'}
				/>
				{t('monitor.preventive_repair')}
			</Label>
			{item.images.map((src, imgIndex) => (
				<div key={`img-${index}-${imgIndex}`} className="relative group">
					<img
						src={src}
						alt={`Uploaded ${imgIndex}`}
						className="w-20 h-auto object-cover rounded-md"
					/>
					{user.type === 'User' && (
						<div
							className="absolute top-0 right-0 rounded-full size-6 flex items-center justify-center cursor-pointer transition-colors bg-[#f5f5f580] hover:bg-gray-bg"
							onClick={() => removeImage(imgIndex, index)}
						>
							<SVGIcon
								className="fill-red h-4 w-auto"
								icon="Delete"
							/>
						</div>
					)}
				</div>
			))}

			<div className="col-span-full flex items-center">
				<div className="pr-3">
					<div
						className={classNames('size-5 rounded-full', {
							'bg-green': configuracaoPreenchida(item),
							'bg-red': !configuracaoPreenchida(item),
						})}
					></div>
				</div>
				<Button
					theme="light"
					className="w-full text-black border-gray-border"
					onClick={() => handleConfigModalVisibility(true, item)}
				>
					Configurações
				</Button>
			</div>
			<Text className="col-span-full text-center text-3xl font-bold" as="h5">
				{item.label} - {item.container?.carrier ?? ''}
			</Text>
			<TextArea
				className="text-sm"
				name={t('misc.description')}
				containerClassName="col-span-full"
				placeholder={t('misc.description')}
				rows={3}
				value={item.observation}
				onChange={(e) => handleDescription(e, index)}
				disabled={user.type !== 'User'}
			/>
		</Card>
		);
	};

	const renderTempRegular = (item: MonitoringForm, index: number) => {
		const temperatureDisabled =
			item.setPoint == null || user.type !== 'User';
		return (
			<>
				{renderStatusSeletor(item, index)}
				<Input
					className={classNames(
						'size-20 px-0 text-center text-3xl font-bold',
						{
							'col-start-2': user.type !== 'User',
						}
					)}
					name="tempCurrent"
					value={item.temperature === null ? 'DRY' : item.temperature}
					onChange={(e) => handleTemperature(e, index)}
					type="text"
					inputMode="decimal"
					disabled={temperatureDisabled}
				/>
				{!temperatureDisabled && (
					<div className="size-20 grid grid-rows-2 gap-2">
						<Button
							className="w-full py-0 text-3xl flex items-center"
							onClick={() => onPlusClick(index)}
						>
							+
						</Button>
						<Button
							className="w-full py-0 text-3xl flex items-center"
							onClick={() => onMinusClick(index)}
						>
							-
						</Button>
					</div>
				)}
			</>
		);
	};

	const renderTempError = (item: MonitoringForm, index: number) => (
		<>
			{renderStatusSeletor(item, index)}
		</>
	);

	const handleImageChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		index: number
	) => {
		const files = event.target.files;
		if (files) handleImageUpload(files, index);
	};

	const handleImageUpload = (newFiles: FileList, index: number) => {
		const fileArray = Array.from(newFiles);
		const readers = fileArray.map((file) => {
			return new Promise<string | null>((resolve) => {
				// Verifica se o arquivo é uma imagem
				if (!file.type.startsWith('image/')) {
					NotificationToast.Warn(
						'Por favor, envie apenas arquivos de imagem.'
					);
					resolve(null);
				} else {
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result as string);
					reader.readAsDataURL(file);
					const updatedData = [...data];
					updatedData[index].files.push(file);
					setData(updatedData);
				}
			});
		});

		// Filtra os arquivos que são imagens válidas e atualiza o estado
		Promise.all(readers).then((loadedImages) => {
			const validImages = loadedImages.filter(
				(image) => image !== null
			) as string[];
			const updatedData = [...data];
			updatedData[index].images.push(...validImages);
			setData(updatedData);
		});
	};

	const removeImage = (index: number, containerIndex: number) => {
		const updatedData = [...data];
		updatedData[containerIndex].files = updatedData[
			containerIndex
		].files.filter((_, i) => i !== index);
		updatedData[containerIndex].images = updatedData[index].images.filter(
			(_, i) => i !== index
		);
		setData(updatedData);
	};

	return (
		<>
			<div
				className={classNames(
					'flex flex-wrap gap-y-6 justify-between',
					{
						hidden: data.length < 1,
					}
				)}
			>
				{data.map((item, index) =>
					supplyReturnAtivado ? (
						renderSupplyReturnCard(item, index)
					) : (
						<Card
						className={classNames('grid grid-cols-3 gap-6 p-6', {
							hidden: !item.label.includes(search),
							'border-2 border-dashed border-red':
								item.possuiMonitoramentoLocal === false,
						})}
						key={`monitoring-${index}`}
					>
						<div
							className={classNames(
								'rounded-md size-20 cursor-pointer flex items-center justify-center',
								{
									'bg-green': item.isChecked,
									'border-default border-dashed':
										!item.isChecked,
									'pointer-events-none': user.type !== 'User',
								}
							)}
							onClick={() => handleIsDone(index)}
						>
							{item.isChecked && (
								<SVGIcon className="w-12 h-auto" icon="Check" />
							)}
						</div>
						{user.type === 'User' && (
							<>
								<div
									className={
										'border-default rounded-md size-20 flex items-center justify-center cursor-pointer'
									}
									onClick={() =>
										handleModalVisibility(true, item)
									}
								>
									<SVGIcon
										className="w-12 h-auto stroke-black"
										icon="Delete"
									/>
								</div>
								<Label
									className="border-default rounded-md size-20 flex items-center justify-center cursor-pointer"
									htmlFor={`container-${index}-img`}
								>
									<input
										className="hidden"
										id={`container-${index}-img`}
										type="file"
										accept="image/*"
										onChange={(e) =>
											handleImageChange(e, index)
										}
										multiple
									/>
									<SVGIcon
										className="w-12 h-auto"
										icon="Camera"
									/>
								</Label>
							</>
						)}
						{[
							'Temperatura Regular',
							'Temperatura em Alerta',
							'Temperatura Crítica',
						].includes(item.cntrStatus)
							? renderTempRegular(item, index)
							: renderTempError(item, index)}

						{item.images.map((src, imgIndex) => (
							<div
								key={`img-${index}-${imgIndex}`}
								className="relative group"
							>
								<img
									src={src}
									alt={`Uploaded ${imgIndex}`}
									className="w-20 h-auto object-cover rounded-md"
								/>
								{user.type === 'User' && (
									<div
										className="absolute top-0 right-0 rounded-full size-6 flex items-center justify-center cursor-pointer transition-colors bg-[#f5f5f580] hover:bg-gray-bg"
										onClick={() =>
											removeImage(imgIndex, index)
										}
									>
										<SVGIcon
											className="fill-red h-4 w-auto"
											icon="Delete"
										/>
									</div>
								)}
							</div>
						))}
						{item.cntrStatus === 'Temperatura Crítica' && (
							<div className="flex gap-x-4 col-span-full place-self-center">
								<RadioOption
									id={`${item.id}-coolingdown-${index}`}
									name={`${item.id}-${index}`}
									label="Coolingdown"
									value="Coolingdown"
									checked={item.tempStatus === 'Coolingdown'}
									onClick={() =>
										handleTempError('Coolingdown', index)
									}
									onChange={() => {}}
									disabled={user.type !== 'User'}
								/>
								<RadioOption
									id={`${item.id}-defrost-${index}`}
									name={`${item.id}-${index}`}
									label="Defrost"
									value="Defrost"
									checked={item.tempStatus === 'Defrost'}
									onClick={() =>
										handleTempError('Defrost', index)
									}
									onChange={() => {}}
									disabled={user.type !== 'User'}
								/>
							</div>
						)}
						<Label
							className={classNames(
								'flex items-center gap-2 col-span-full cursor-pointer select-none w-fit place-self-center',
								{
									'text-primary': item.reparoPreventivo,
									'text-gray': !item.reparoPreventivo,
									'pointer-events-none': user.type !== 'User',
								}
							)}
							htmlFor={`reparo-preventivo-${index}`}
						>
							<input
								className="relative appearance-none w-4 h-4 text-primary border border-gray-border rounded cursor-pointer checked:border-primary checked:after:absolute checked:after:bg-primary checked:after:size-2 checked:after:rounded checked:after:top-1/2 checked:after:left-1/2 checked:after:-translate-x-1/2 checked:after:-translate-y-1/2"
								id={`reparo-preventivo-${index}`}
								type="checkbox"
								checked={!!item.reparoPreventivo}
								onChange={() => handleReparoPreventivo(index)}
								disabled={user.type !== 'User'}
							/>
							{t('monitor.preventive_repair')}
						</Label>
						<div className="flex gap-2 justify-around col-span-full">
							<div className="flex items-center gap-1">
								<div className="size-6 flex justify-center items-center bg-black rounded-md">
									<SVGIcon
										className="h-4 w-auto fill-white"
										icon="Drop"
									/>
								</div>
								<Input
									className="w-10 px-1 py-1 text-xs text-center"
									type="number"
									value={item.humidity || ''}
									onChange={(e) =>
										handlePercentage(e, 'humidity', index)
									}
									disabled={user.type !== 'User'}
								/>
								<Text className="text-xs">%</Text>
							</div>
							<div className="flex items-center gap-1">
								<div className="size-6 flex justify-center items-center bg-primary-light rounded-md">
									<SVGIcon
										className="h-4 w-auto fill-white"
										icon="Cold"
									/>
								</div>
								<Input
									className="w-10 px-1 py-1 text-xs text-center"
									type="number"
									value={item.ventilation || ''}
									onChange={(e) =>
										handlePercentage(
											e,
											'ventilation',
											index
										)
									}
									disabled={user.type !== 'User'}
								/>
								<Text className="text-xs">%</Text>
							</div>
						</div>
						<div className="col-span-full flex items-center">
							<div className="pr-3">
								<div
									className={classNames('size-5 rounded-full', {
										'bg-green': configuracaoPreenchida(item),
										'bg-red': !configuracaoPreenchida(item),
									})}
								></div>
							</div>
							<Button
								theme="light"
								className="w-full text-black border-gray-border"
								onClick={() => handleConfigModalVisibility(true, item)}
							>
								Configurações
							</Button>
						</div>
						<Text
							className="col-span-full text-center font-semibold"
							as="h5"
						>
							{item.label} - {item.container?.carrier ?? ''}
						</Text>
						<TextArea
							className="text-sm"
							name={t('misc.description')}
							containerClassName="col-span-full"
							placeholder={t('misc.description')}
							rows={3}
							value={item.observation}
							onChange={(e) => handleDescription(e, index)}
							disabled={user.type !== 'User'}
						/>
					</Card>
					)
				)}
				
			</div>
			<Modal
				className="flex flex-col gap-4 w-fit px-10 py-7 bg-gray-bg"
				isModalVisible={isConfigModalVisible}
				onClose={() => handleConfigModalVisibility(false)}
			>
				<div className="grid grid-cols-[auto_7.5rem] gap-x-10 gap-y-3 items-center">
					<Text className="font-bold">Ventilação</Text>
					<div className="flex items-center gap-2">
						<Input
							className="w-20 px-2 text-center font-bold"
							type="number"
							value={modalData?.ventilation ?? ''}
							onChange={(e) => {
								const index = data.findIndex(
									(item) => item.idContainer === modalData?.idContainer
								);
								if (index > -1)
									handlePercentage(e, 'ventilation', index);
							}}
							disabled={user.type !== 'User'}
						/>
						<Text className="font-bold">%</Text>
					</div>
					<Text className="font-bold">Umidade</Text>
					<div className="flex items-center gap-2">
						<Input
							className="w-20 px-2 text-center font-bold"
							type="number"
							value={modalData?.humidity ?? ''}
							onChange={(e) => {
								const index = data.findIndex(
									(item) => item.idContainer === modalData?.idContainer
								);
								if (index > -1)
									handlePercentage(e, 'humidity', index);
							}}
							disabled={user.type !== 'User'}
						/>
						<Text className="font-bold">%</Text>
					</div>
					<Text className="font-bold">Ctrl Atmosf</Text>
					<Button
						theme="light"
						className="w-20 text-black border-gray-border"
						onClick={() => {
							const index = data.findIndex(
								(item) => item.idContainer === modalData?.idContainer
							);
							if (index > -1) handleAtmosferaControlada(index);
						}}
						disabled={user.type !== 'User'}
					>
						{modalData?.atmosferaControlada ? 'Sim' : 'Não'}
					</Button>
					<Text className="font-bold">Nivel CO²</Text>
					<div className="flex items-center gap-2">
						<Input
							className="w-20 px-2 text-center font-bold"
							type="number"
							value={modalData?.nivelCo2 ?? ''}
							onChange={(e) => {
								const index = data.findIndex(
									(item) => item.idContainer === modalData?.idContainer
								);
								if (index > -1)
									handlePercentage(e, 'nivelCo2', index);
							}}
							disabled={user.type !== 'User'}
						/>
						<Text className="font-bold">%</Text>
					</div>
				</div>
			</Modal>
			<Modal
				className="flex flex-col items-center justify-center gap-4"
				isModalVisible={isModalVisible}
				onClose={() => handleModalVisibility(false)}
				hideClose
			>
				<SVGIcon
					className="w-16 h-auto stroke-red justify-self-center"
					icon="Delete"
				/>
				<Text className="text-center text-lg text-black">
					{t('port_management.container')} <b>{modalData?.label}</b>{' '}
					{t('alert.warn.remove')}
				</Text>
				<Text className="text-center text-gray-label">
					{t('alert.warn.confirm')}
				</Text>
				<div className="flex justify-around gap-4">
					<Button
						theme="light"
						className="w-40"
						onClick={() => handleModalVisibility(false)}
					>
						{t('misc.cancel')}
					</Button>
					<Button
						theme="red"
						className="w-40"
						onClick={() => handleDeleteContainer()}
					>
						{t('misc.confirm')}
					</Button>
				</div>
			</Modal>
		</>
	);
};

export default MonitoringContainers;
